import { Language, LanguageType, SearchPredicate } from '@nx-smartmonkey/shared/domain';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import * as routing from '../../../../constants/Routing';
import { useRouterQuery } from '../../../../hooks/useRouterQuery';
import { BackofficeConstants } from '../../context/actions';
import { useBackofficeContext } from '../../context/context';

type UseSearchTranslationsPredicatesResponse = {
  language: LanguageType;
  predicates: SearchPredicate[];
  predicatesBase64: string;
  setPredicates: (predicates: SearchPredicate[]) => void;
};

export const useSearchTranslationsPredicates = (): UseSearchTranslationsPredicatesResponse => {
  const navigate = useNavigate();
  const location = useLocation();
  const { backofficeState, backofficeDispatch } = useBackofficeContext();
  const urlParams = useRouterQuery();

  const language: LanguageType = (urlParams.get(`language`) ?? Language.getDefaultLanguage().value) as LanguageType;

  useEffect(() => {
    // Only adds the predicates base64 string in the url if we are already in one of the planner plans pages.
    if (location.pathname.includes(routing.BACKOFFICE.TRANSLATIONS)) {
      const predicatesParam = urlParams.get(`predicates`);
      if (predicatesParam !== backofficeState.translations.predicatesBase64) {
        navigate(
          `${routing.BACKOFFICE.TRANSLATIONS}?predicates=${backofficeState.translations.predicatesBase64}&language=${language}`
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backofficeState.translations.predicatesBase64]);

  const setPredicatesFunc = (newPredicates: Array<SearchPredicate>) => {
    const base64 = SearchPredicate.predicatesToBase64(newPredicates);
    backofficeDispatch({
      type: BackofficeConstants.SET_SEARCH_TRANSLATIONS_PREDICATES,
      payload: {
        predicates: newPredicates,
        predicatesBase64: base64,
      },
    });

    if (backofficeState.translations.predicatesBase64 !== base64) {
      navigate(`${routing.BACKOFFICE.TRANSLATIONS}?predicates=${base64}&language=${language}`);
    } else {
      navigate(
        `${routing.BACKOFFICE.TRANSLATIONS}?predicates=${backofficeState.translations.predicatesBase64}&language=${language}`
      );
    }
  };

  return {
    language,
    predicates: backofficeState.translations.predicates,
    predicatesBase64: backofficeState.translations.predicatesBase64,
    setPredicates: setPredicatesFunc,
  };
};
